import HighchartsReact from 'highcharts-react-official'
import Highcharts, { ChartClickEventObject, Series, XAxisPlotBandsEventsOptions } from 'highcharts'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Scale } from '../../../pages/trainings/ExamView'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import {
    convertSpeed,
    convertSpeedToNb,
    convertStrideLength,
    getSpeedForChart,
    getSpeedTooltipForChart
} from '../../../shared/utils/conversion'
import { get, isArray, isEmpty, isNull, max, mean, min, set, throttle, toNumber } from 'lodash'
import dayjs from 'dayjs'
import Duration from 'dayjs/plugin/duration'
import {
    Box,
    Button,
    IconButton,
    Menu,
    Paper,
    Popper,
    Snackbar,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material'
import { useChart } from '../../../hooks/useChart'
import { mergeArr, startTimeFrom0 } from '../../../shared/utils/MapSection'
import { TrainingSetting } from '../../../shared/types/trainingSetting.type'
import Hideable from '../../global/Hideable'
import { useAuth } from '../../../hooks/useAuth'
import { Icon } from '@iconify/react'
import {
    CHART_COLOR_BPM,
    CHART_COLOR_BPM_WITH_OPACITY,
    CHART_COLOR_SPEED,
    CHART_COLOR_STRIDE_FREQUENCY,
    CHART_COLOR_STRIDE_LENGTH
} from '../../../enums/chatColors.enums'
import { Point } from 'highcharts'
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useTranslation } from 'react-i18next'
import { binarySearch } from '../../../shared/utils/arrays'
import { CreateChukkerDto } from '../../../dto/createChukkerDto'
import { TUser } from '../../../shared/types/user.type'
import { Chukkers } from '../../../shared/types/chukkers.type'
import { createPlotBandsFromChukkers } from '../../../shared/utils/myTrainings'
import { scale } from '../../../enums/scale.enum'
import MenuItem from 'antd/es/menu/MenuItem'
import gsap from 'gsap'
import { Edit } from '@mui/icons-material'
import { CloseIcon } from '../../../theme/overrides/CustomIcons'
import { GridDeleteIcon } from '@mui/x-data-grid'
import { TPlatformConfiguration } from '../../../shared/types/platformConfiguration'

NoDataToDisplay(Highcharts)
dayjs.extend(Duration)


type GraphsProps = {
    speed: number[]
    bpm: number[],
    distance: number[],
    loCardio: number[],
    time: number[],
    scaleOption: Scale,
    strideFrequency: number[],
    strideLength: number[],
    currentIndex: number,
    flIndex?: number | null,
    isFinishLineActive: boolean,
    onExtremesChanged: (value: number) => void,
    displayAlertBadCardio: boolean,
    onIndexChange: (index: number) => void,
    hintValue?: string
    labelsColor?: string
    isChukkerSelectionEnabled?: boolean,
    isChukkerEditionEnabled?: boolean,
    saveChukker?: (data: CreateChukkerDto, plotId?: number | null) => void,
    chukkers?: Chukkers,
    toggleChukkerEditMode?: (value: boolean) => void,
    onDeleteChukker?: (value: number) => void,
    editModeEnabled?: boolean,
    plotBandId: number | null,
    setPlotBandId?: (value: number | null) => void,
    printable?: boolean,
    mergedBpmSpeed: boolean,
    mergedStride: boolean,
    adaptiveDisplay?: boolean
}


/**
 * Override the reset function, we don't need to hide the tooltips and crosshairs.
 */
Highcharts.Pointer.prototype.reset = function () {
    return undefined;
};
/* @ts-ignore */
Highcharts.Point.prototype.highlight = function highlight(event) {
    event = this.series?.chart.pointer.normalize(event);
    this.onMouseOver(); // Show the hover marker
    this.series?.chart.xAxis[0].drawCrosshair(event, this); // Show the crosshair
};

function Graphs({
    plotBandId,
    setPlotBandId,
    onDeleteChukker,
    editModeEnabled,
    toggleChukkerEditMode,
    chukkers,
    saveChukker,
    isChukkerSelectionEnabled = false,
    isChukkerEditionEnabled = false,
    labelsColor = '#fff',
    hintValue,
    onIndexChange,
    bpm,
    distance,
    speed,
    scaleOption,
    time,
    strideFrequency,
    strideLength,
    currentIndex,
    flIndex,
    isFinishLineActive,
    loCardio,
    onExtremesChanged,
    displayAlertBadCardio,
    printable = false,
    mergedBpmSpeed,
    mergedStride,
    adaptiveDisplay = false
}: GraphsProps) {


    const metric = useSelector<RootState>(state => state.user.user?.unit_system) as "metric" | "imperial" | undefined
    const { addFinishLine } = useChart()
    const bpmRef = useRef<HighchartsReact.RefObject>(null)
    const bpmSpeedRef = useRef<HighchartsReact.RefObject>(null)
    const speedRef = useRef<HighchartsReact.RefObject>(null)
    const userSettings = useSelector<RootState>(state => state.user?.user?.training_settings) as TrainingSetting
    const { isUserTypePolo } = useAuth()
    const strideRef = useRef<HighchartsReact.RefObject>(null)
    const freqRef = useRef<HighchartsReact.RefObject>(null)
    const lengthRef = useRef<HighchartsReact.RefObject>(null)
    const divRef = useRef<HTMLDivElement | null>(null)
    const { getSpeedUnit, getUserSpeedUnit, getUserAmplitudeUnit } = useAuth()
    const [isZoomActive, setIsZoomActive] = useState(false)
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const platformConfigurations = useSelector<RootState>(state => state.user.platformConfigurations) as TPlatformConfiguration[]

    const [plotBandLabelText, setPlotBandLabelText] = useState('')


    const popupRef = useRef<HTMLDivElement | null>(null);

    const { t } = useTranslation()


    const getMaxYValueSpeed = useMemo(() => {
        const DEFAULT_MAX_VALUE = 249
        try {


            let findMaxValue = platformConfigurations.find(config => config.key === 'MAX_VALUE_SPEED_CHART_KMH')
            console.log({ findMaxValue });

            return findMaxValue?.value ? (Number(findMaxValue?.value) / 0.3) : DEFAULT_MAX_VALUE
        } catch (error) {
            console.error(error);
            return DEFAULT_MAX_VALUE
        }
    }, [platformConfigurations])

    const BaseOptions = {
        credits: {
            enabled: false
        },
        chart: {
            type: 'area',
            zoomType: "x",
            pinchType: 'x', // allows pinch zoom on mobile
            zoomOnSingleTouch: false, // ← disables zoom on single touch
            panning: {
                enabled: true,
                type: 'x'
            },
            spacingLeft: 0,
            spacingRight: 0,
            marginTop: 80,
            marginLeft: isUserTypePolo ? 120 : 70,
            marginRight: isSmallScreen ? (isUserTypePolo ? 10 : 40) : 80,
            backgroundColor: 'transparent',
            resetZoomButton: {
                theme: {
                    display: 'none' // Hides the button completely
                }
            },
            animation: false,

            events: {
                selection: function (this: Highcharts.Chart, event: Highcharts.SelectEventObject) {

                    // Extract the zoom range from the event
                    const zoomMin = get(event, 'xAxis[0].min', undefined);
                    const zoomMax = get(event, 'xAxis[0].max', undefined);

                    const chartName = (this.options.chart as any).name;


                    if (isChukkerSelectionEnabled &&
                        (chartName === 'bpm' || chartName === 'speedbpm')) {
                        if (zoomMin === undefined || zoomMax === undefined) return;

                        const series = this.series?.[0];
                        if(!series) return
                        const xData = (series as any).xData as number[];

                        // Binary search for first index >= min
                        const startIndex = binarySearch(xData, zoomMin, true);

                        // Binary search for last index <= max
                        const endIndex = binarySearch(xData, zoomMax, false);


                        if (typeof saveChukker === 'function') saveChukker({
                            manual_index_start: startIndex,
                            manual_index_end: endIndex,
                            training: ""
                        }, editModeEnabled && plotBandId ? plotBandId : null)


                        console.log('Start index:', startIndex);
                        console.log('End index:', endIndex);


                        (event as any).preventDefault();
                        return false

                    }


                    if (zoomMin !== undefined && zoomMax !== undefined) {
                        syncZoomAcrossCharts(zoomMin, zoomMax);
                    }


                    // Prevent the default zoom behavior
                    return false;

                },
                click: function (event: ChartClickEventObject): void {
                    updateIndexByClick(this, event)
                },
                load: function (this: Highcharts.Chart) {
                    const chart = this;
                    const bg = chart.options.chart!.backgroundColor || '#ffffff';
                    const colorStr = Highcharts.color(bg)?.get();

                    // Parse to RGBA

                    if (typeof colorStr === 'string') {
                        const match = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);

                        if (match) {
                            const r = parseInt(match[1], 10);
                            const g = parseInt(match[2], 10);
                            const b = parseInt(match[3], 10);

                            const brightness = r * 0.299 + g * 0.587 + b * 0.114;
                            const labelColor = brightness > 186 ? '#222' : 'white';


                            this.update({
                                xAxis: {
                                    labels: {
                                        style: {
                                            color: adaptiveDisplay ? '#222' : labelColor
                                        }
                                    }
                                }
                            });

                        }


                    }
                },
                redraw: () => handleFinishLine()
            }
        },
        lang: {
            noData: 'No data to display'
        },
        noData: {
            style: {
                fontSize: '16px',
                fontWeight: 'bold'
            }
        },
        boost: {
            useGPUTranslations: true,

        },
        plotOptions: {
            series: {
                states: {
                    hover: {
                        lineWidthPlus: 0 // Prevents line width from increasing on hover

                    }
                },
                marker: {
                    enabled: false,
                },
                fillOpacity: 0.3,
                point: {
                    events: {
                        click: function (this: Point, event: Highcharts.PointClickEventObject): void {
                            const chart = this.series?.chart; // Get the chart instance
                            
                            if(!chart) return
                            
                            updateIndexByClick(chart, event)
                        }
                    }
                }
            },
            area: {

                lineWidth: 1,
                states: {
                    inactive: {
                        opacity: 1
                    },
                    hover: {
                        lineWidth: 1,
                        halo: { size: 0 }

                    },

                },

            },
        },
        series: {
            marker: {
                enabled: false
            },
            states: {
                inactive: {
                    opacity: 1
                }
            }

        },

        title: {
            align: 'left',
            style: {
                color: 'white'
            }
        },
        yAxis: {

            title: "",
            gridLineColor: 'transparent',
            type: 'linear',
            endOnTick: false,
            className: 'highcharts-labels',
            alignTicks: true,
        },
        legend: { enabled: false },
        tooltip: {
            backgroundColor: 'transparent',
            borderWidth: 0,
            enabled: true,
            shared: true,
            followPointer: false,
            followTouchMove: false,
            positioner: function (
                this: Highcharts.Tooltip,
                labelWidth: number,
                labelHeight: number,
                point: Highcharts.Point
            ): Highcharts.PositionObject {
                const chart = this.chart;
                const padding = 10;
                const isMobile = window.innerWidth < 768;

                // Adjust Y position to avoid overlapping the title on mobile
                const topOffset = isMobile ? 20 : padding;

                return {
                    x: chart.chartWidth - labelWidth - padding,
                    y: topOffset
                };
            },
            shadow: false,
            style: {
                maxWidth: '100px',
                fontWeight: 'bold',
                fontSize: '12px',
                color: labelsColor
            }
        },
        responsive: {
            rules: [{
                condition: {
                    maxWidth: 600
                },
                chartOptions: {

                    title: {
                        style: {
                            fontSize: '14px', // Responsive chart title
                        },
                        margin: 20 // Space BELOW title

                    },
                    xAxis: {
                        labels: {
                            style: {
                                fontSize: '10px' // Responsive x-axis labels
                            }
                        }
                    }
                }
            }]
        }

    }


    const syncZoomAcrossCharts = throttle((min: number, max: number) => {
        const charts = getCharts();
        charts.forEach(chart => {
            if (chart) {
                chart.xAxis[0].setExtremes(min, max, true, false, { trigger: false });
            }
        });
    }, 300)

    const updateIndexByClick = (chart: any, event: ChartClickEventObject | Highcharts.PointClickEventObject) => {
        const series = chart.series?.[0] as Series; // Get the first series

        if(!series) return

        // Use searchPoint to find the closest point
        const closestPoint = series.searchPoint(event, true) as Point | null;

        if (closestPoint) {
            onIndexChange(closestPoint.index)
        } else {
            console.warn('No closest point found');
        }
    }

    const formattedTime = useMemo(() => {
        return startTimeFrom0(time)
    }, [time])


    const getCharts = () => {
        let chart1 = bpmRef.current?.chart
        let chart2 = speedRef.current?.chart
        let chart3 = strideRef.current?.chart
        let chart4 = freqRef.current?.chart
        let chart5 = lengthRef.current?.chart
        let chart6 = bpmSpeedRef.current?.chart

        let charts = [chart1, chart2, chart3, chart4, chart5, chart6]
        return charts
    }

    const mouseOverCallback = useCallback((e: MouseEvent | TouchEvent) => {


        getCharts().forEach((chart, i) => {
            let event = chart?.pointer.normalize(e);
            if (!event) return
            let series = chart?.series?.[0]
            if (!series) return


            // Reset any active tooltip and crosshair
            chart?.pointer.reset();  // This will hide the tooltip and crosshair
            // Create a new event object with only the x-coordinate
            const eventWithOnlyX = {
                ...event,
                chartX: event.chartX, // Ensure the x-axis position is intact
                chartY: undefined, // Remove the y-axis position
            };
            const point = event && series?.searchPoint(eventWithOnlyX, true);
            if (point) {
                /* @ts-ignore */
                point.highlight(eventWithOnlyX);
            }

        })


    }, [])

    useEffect(() => {


        updateChartWithCurrentIndex(currentIndex)

    }, [currentIndex])


    const updateChartWithCurrentIndex = throttle((currentIndex: number) => {
        getCharts().forEach((chart, i) => {
            chart?.series?.forEach(function (series) {


                const point = get(series, `points[${currentIndex}]`, null)
                if (point) {
                    /* @ts-ignore */
                    point.onMouseOver?.()
                }
            });

        })
    }, 400)


    useEffect(() => {
        if (!divRef.current) return;

        // Use the same handler reference for both mousemove and touchmove
        const handler = (e: MouseEvent | TouchEvent) => mouseOverCallback(e);

        divRef.current.addEventListener('mousemove', handler);
        divRef.current.addEventListener('touchmove', handler, { passive: true });

        return () => {
            divRef.current?.removeEventListener('mousemove', handler);
            divRef.current?.removeEventListener('touchmove', handler);
        };
    }, [mouseOverCallback]);

    const distanceConverted = useMemo(() => {
        if (isEmpty(distance)) return []

        return distance.slice(1).map(d => convertSpeedToNb(metric, d))
    }, [distance.length, metric])

    const timeConverted = useMemo(() => {
        if (isEmpty(formattedTime)) return []

        return formattedTime.slice(1).map(t => t / 60)
    }, [formattedTime.length])

    const handlePlotBandClick = (e: MouseEvent, options: any) => {
        if (typeof setPlotBandId === 'function') setPlotBandId(Number(options.id));


        // ✅ Extract label text
        const labelText = options.label?.text ? `Chukker ${options.label?.text}` : 'No label text';


        setPlotBandLabelText(labelText)

        // Get click coords
        const x = e.clientX;
        const y = e.clientY;

        if (popupRef.current) {
            gsap.fromTo(
                popupRef.current,
                { opacity: 0, x, y: y - 50 },  // start slightly above with 0 opacity
                {
                    opacity: 1, x, y, duration: 1,
                    ease: "elastic.out(1, 0.5)",  // springy bounce effect
                } // animate down + fade in
            );
        }
    };


    const plotBandsEvent: XAxisPlotBandsEventsOptions = !isChukkerEditionEnabled ? {} : {
        click(e: any) {
            handlePlotBandClick(e, this.options); // ✅ Capture `this.options.id`
        }
    }


    const chukkersToDisplay = useMemo(() => {
        if (editModeEnabled && plotBandId) {
            return chukkers?.filter(c => c.id === plotBandId);
        }
        return chukkers;
    }, [editModeEnabled, plotBandId, chukkers]);

    const plotBands = useMemo(() => {

        let plotIndex = null

        const plot = editModeEnabled && plotBandId ? chukkers?.findIndex(chukker => chukker.id == plotBandId) : null

        //On Edit Mode we want not to start index from 0 but keep the same index in same order based on previous chukkers
        if (plot && plot !== -1) {
            plotIndex = (plot + 1).toString()
        }


        return createPlotBandsFromChukkers(
            chukkersToDisplay || [],
            timeConverted,
            distanceConverted,
            scaleOption,
            plotBandsEvent,
            plotBandId,
            plotIndex
        );
    }, [
        chukkersToDisplay,
        timeConverted,
        distanceConverted,
        scaleOption,
        plotBandsEvent,
        plotBandId,
        editModeEnabled,
        chukkers,
    ]);


    useEffect(() => {

        handleFinishLine()

    }, [flIndex, scaleOption, timeConverted, distanceConverted, isFinishLineActive, userSettings, isFinishLineActive])


    const handleFinishLine = () => {
        if (!isFinishLineActive) return

        addFinishLines()
    }


    useEffect(() => {
        resetZoom()
    }, [scaleOption])


    const removeFinishLines = () => {
        try {
            let charts = getCharts()
            charts.forEach(chart => {
                let svgs = get(chart, 'svgs', [])

                if (!isEmpty(svgs)) {
                    /* @ts-ignore */
                    svgs.forEach(svg => svg?.destroy())
                }
                /* @ts-ignore */
                set(chart, 'svgs', [])

            })
        } catch (err) {
            console.warn('cannot delete svgs')
        }
    }


    const addFinishLines = () => {
        if (!isFinishLineActive) return
        removeFinishLines()

        addFinishLine(flIndex ?? null, scaleOption, distanceConverted, timeConverted, bpmRef.current?.chart)


        addFinishLine(flIndex ?? null, scaleOption, distanceConverted, timeConverted, speedRef.current?.chart)


        addFinishLine(flIndex ?? null, scaleOption, distanceConverted, timeConverted, strideRef.current?.chart)
        addFinishLine(flIndex ?? null, scaleOption, distanceConverted, timeConverted, freqRef.current?.chart)
        addFinishLine(flIndex ?? null, scaleOption, distanceConverted, timeConverted, lengthRef.current?.chart)
        addFinishLine(flIndex ?? null, scaleOption, distanceConverted, timeConverted, bpmSpeedRef.current?.chart)


    }


    const checkIfDataAllZero = (data: number[]) => {
        if (isEmpty(data)) return []
        return data.every(i => i === 0) ? [] : data

    }

    console.log({ getMaxYValueSpeed });


    const optionsBpm = useMemo(() => {
        let xAxisData = scaleOption === 'DISTANCE' ? distanceConverted : timeConverted;
        let data = isEmpty(checkIfDataAllZero(bpm)) || isEmpty(checkIfDataAllZero(xAxisData)) ? [] : mergeArr(xAxisData, bpm);


        let groupedLoCardio: { start: number, end: number, opacity: number }[] = []


        if (!isEmpty(loCardio)) {
            let index = 0;

            while (index < loCardio.length) {
                let current_opacity = loCardio[index] === -1 ? 0.2 : 0.5;

                if (groupedLoCardio.length === 0 || groupedLoCardio[groupedLoCardio.length - 1].opacity !== current_opacity) {
                    // Start a new zone
                    groupedLoCardio.push({ start: index, end: index, opacity: current_opacity });
                } else {
                    // Extend the current zone
                    groupedLoCardio[groupedLoCardio.length - 1].end = index;
                }

                index++;
            }
        }

        let zonesLo: any[] = []

        if (!isEmpty(groupedLoCardio)) {
            zonesLo = groupedLoCardio.map((zone) => {
                let baseColor = CHART_COLOR_BPM_WITH_OPACITY(zone.opacity);
                let fullColor = CHART_COLOR_BPM_WITH_OPACITY(1);

                return {
                    color: zone.opacity >= 0.5 ? fullColor : baseColor,
                    fillColor: {
                        linearGradient: {
                            x1: 0,
                            y1: 0,
                            x2: 0,
                            y2: 1
                        },
                        stops: [
                            [0, baseColor],
                            [1, CHART_COLOR_BPM_WITH_OPACITY(0.2)]
                        ]
                    },
                    value: xAxisData[zone.end] ?? xAxisData[xAxisData.length - 1] // Ensure it does not go out of bounds
                };
            });
        }


        return {
            ...BaseOptions,
            chart: {
                ...BaseOptions.chart,
                name: 'bpm',
                // styledMode: true,
            },
            title: {
                ...BaseOptions.title, text: t('map:heartRate'), style: {
                    ...(BaseOptions.title?.style || {}),
                    color: adaptiveDisplay ? '#222' : isSmallScreen ? CHART_COLOR_BPM : "white"
                }
            },
            yAxis: {
                ...BaseOptions.yAxis,
                min: 0,
                max: 250,


                tickPositions: [0, 62, 125, 187, 250], // Exactly 4 intervals, 5 ticks
                title: isSmallScreen ? undefined : { text: t('map:heartRate'), style: { color: CHART_COLOR_BPM } },
                gridLineColor: 'transparent',
                labels: {
                    style: {
                        color: CHART_COLOR_BPM
                    }
                }
            },

            xAxis: {
                plotBands,
                events: {

                    afterSetExtremes: (event: Highcharts.AxisSetExtremesEventObject) => {

                        let min = event.min
                        let max = event.max


                        if (min < 0) {
                            max -= Math.abs(min)
                            min = 0
                        }

                        // Early exit for empty charts (no data points)
                        const axis = event.target as unknown as Highcharts.Axis;
                        const chart = axis.chart;
                        const hasData = chart.series?.some(series => series.data.length > 0);


                        if (isNull(event?.dataMin) || isNull(event?.dataMax)) {
                            return
                        }


                        const isZoomed = min > event?.dataMin || max < event?.dataMax;

                        onExtremesChanged(max - min)

                        if (hasData) setIsZoomActive(isZoomed)

                        // ✅ Sync to other charts on mobile pinch (but skip if programmatic)
                        if (!isChukkerSelectionEnabled && event.trigger) {

                            syncZoomAcrossCharts(min, max);
                        }
                    }

                },
                crosshair: {
                    width: 1,
                },
                type: 'linear',
                labels: {
                    style: {
                        color: labelsColor
                    },
                    format: `{value} ${scaleOption === 'TIME' ? ' mn' : metric === 'metric' ? ' km' : ' mi'}`,

                },

            },
            tooltip: {
                ...BaseOptions.tooltip, shared: true,
                useHTML: true,
                style: {
                    color: labelsColor
                },
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    let data = get(this, 'series.processedYData', [])
                    if (isZoomActive) {
                        if (data.length > 0) {
                            let minVal = min(data)
                            let maxVal = max(data)
                            let avg = Math.round(mean(data))

                            return `${this.y} ${t('units:bpm')}
            <br/>
           <span style="font-size:10px;font-weight:400"> (${t('minimumAbbreviation')}: ${minVal} -  ${t('meanAbbreviation')}: ${avg} -  ${t('maximumAbbreviation')}: ${maxVal})</span>`

                        } else {
                            return this.y?.toString().concat(` ${t('units:bpm')}`)
                        }
                    } else {
                        return this.y?.toString().concat(` ${t('units:bpm')}`)
                    }
                }
            },
            series: [
                {
                    data: data,
                    nextChartData: true,
                    zoneAxis: "x",
                    zones: zonesLo,
                    // lineWidth: 2,
                    color: CHART_COLOR_BPM_WITH_OPACITY(0.4),
                    fillColor: {
                        linearGradient: {
                            x1: 0,
                            y1: 0,
                            x2: 0,
                            y2: 1
                        },
                        stops: [
                            [0, CHART_COLOR_BPM_WITH_OPACITY(0.4)],
                            [1, CHART_COLOR_BPM_WITH_OPACITY(0.2)]
                        ]
                    },
                },

            ]
        }
    }, [bpm, distanceConverted, timeConverted, scaleOption, flIndex, isZoomActive, isFinishLineActive, t, isChukkerSelectionEnabled, plotBands, plotBandId, editModeEnabled, isSmallScreen])


    const optionsSpeed = useMemo(() => {
        return {
            ...BaseOptions,
            chart: {
                ...BaseOptions.chart,

            },
            title: {
                ...BaseOptions.title, text: t('graph:vitesse'), style: {
                    ...(BaseOptions.title?.style || {}),
                    color: adaptiveDisplay ? '#222' : isSmallScreen ? CHART_COLOR_SPEED : "white"
                }
            },
            yAxis: {
                ...BaseOptions.yAxis,
                tickAmount: 4,
                // 1. Critical: Prevent Highcharts from syncing this axis scale with others
                alignTicks: false,

                // 2. Lock the max to your DB value (e.g., 183.33)
                max: getMaxYValueSpeed,

                // 3. Prevent rounding up to the next "nice" number (like 240)
                endOnTick: false,
                startOnTick: false,

                // 4. Force exact positions so it doesn't calculate 0, 60, 120, 180, 240
                tickPositions: [
                    0,
                    getMaxYValueSpeed * 0.33,
                    getMaxYValueSpeed * 0.66,
                    getMaxYValueSpeed
                ],

                title: isSmallScreen ? undefined : { text: t('graph:vitesse'), style: { color: CHART_COLOR_SPEED } },
                labels: {
                    style: { color: CHART_COLOR_SPEED },
                    /* @ts-ignore */
                    formatter: function () {
                        /* @ts-ignore */
                        const val = toNumber(getSpeedForChart(toNumber(this.value), getSpeedUnit()?.value ?? ""));
                        return val.toFixed(0);
                    }
                }
            },
            tooltip: {
                ...BaseOptions.tooltip,
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    let data = get(this, 'series.processedYData', [])

                    if (isZoomActive) {
                        if (data.length > 0) {
                            let minVal = getSpeedTooltipForChart(toNumber(getSpeedForChart(toNumber(min(data)), getSpeedUnit()?.value ?? "")), getSpeedUnit()?.value ?? "")
                            let maxVal = getSpeedTooltipForChart(toNumber(getSpeedForChart(toNumber(max(data)), getSpeedUnit()?.value ?? "")), getSpeedUnit()?.value ?? "")
                            let avg = getSpeedTooltipForChart(toNumber(getSpeedForChart(toNumber(Math.round(mean(data))), getSpeedUnit()?.value ?? "")), getSpeedUnit()?.value ?? "")

                            return `${getSpeedTooltipForChart(toNumber(getSpeedForChart(toNumber(this.y), getSpeedUnit()?.value ?? "")), getSpeedUnit()?.value ?? "")} 
              <br/>
             <span style="font-size:10px;font-weight:400"> (${t('minimumAbbreviation')}: ${minVal} -  ${t('meanAbbreviation')}: ${avg} -  ${t('maximumAbbreviation')}: ${maxVal})</span>`

                        } else {

                            /* @ts-ignore */
                            return getSpeedTooltipForChart(getSpeedForChart(toNumber(this.y), getSpeedUnit()?.value ?? ""), getSpeedUnit()?.value ?? "")

                        }
                    } else {
                        /* @ts-ignore */
                        return getSpeedTooltipForChart(getSpeedForChart(toNumber(this.y), getSpeedUnit()?.value ?? ""), getSpeedUnit()?.value ?? "")

                    }
                }
            },
            xAxis: {
                events: {
                    afterSetExtremes: (event: Highcharts.AxisSetExtremesEventObject) => {

                        let min = event.min
                        let max = event.max


                        if (min < 0) {
                            max -= Math.abs(min)
                            min = 0
                        }

                        // Early exit for empty charts (no data points)
                        const axis = event.target as unknown as Highcharts.Axis;
                        const chart = axis.chart;
                        const hasData = chart.series?.some(series => series.data.length > 0);


                        if (isNull(event?.dataMin) || isNull(event?.dataMax)) {
                            return
                        }

                        const isZoomed = min > event?.dataMin || max < event?.dataMax;

                        onExtremesChanged(max - min)


                        if (hasData) setIsZoomActive(isZoomed)

                        // ✅ Sync to other charts on mobile pinch (but skip if programmatic)
                        if (!isChukkerSelectionEnabled && event.trigger) {

                            syncZoomAcrossCharts(min, max);
                        }
                    }

                },
                crosshair: {
                    width: 1,
                },
                type: 'linear',
                labels: {
                    format: `{value} ${scaleOption === 'TIME' ? ' mn' : metric === 'metric' ? ' km' : ' mi'}`,
                    style: {
                        color: labelsColor
                    },

                }
            },
            series: [
                {
                    data: isEmpty(checkIfDataAllZero(speed)) ? [] : mergeArr(scaleOption === 'DISTANCE' ? distanceConverted : timeConverted, speed),
                    fillColor: {
                        linearGradient: {
                            x1: 0,
                            y1: 0,
                            x2: 0,
                            y2: 1
                        },
                        stops: [
                            [0, new (Highcharts as any).Color(CHART_COLOR_SPEED).setOpacity(0.4).get('rgba')],
                            [1, new (Highcharts as any).Color(CHART_COLOR_SPEED).setOpacity(0.2).get('rgba')]
                        ]
                    },
                    point: {},
                    nextChartData: true,
                }
            ]
        }
    }, [speed, distanceConverted, timeConverted, scaleOption, isZoomActive, isFinishLineActive, t, isChukkerSelectionEnabled, isSmallScreen, getMaxYValueSpeed])

    const optionsBpmSpeed = useMemo(() => {

        let xAxisData = scaleOption === 'DISTANCE' ? distanceConverted : timeConverted;

        let groupedLoCardio = loCardio.reduce((acc: { value: number; opacity: number }[], val, idx) => {
            if (val === -1 && (acc.length === 0 || acc[acc.length - 1].opacity !== 0.2)) {
                acc.push({ value: idx, opacity: 0.2 });
            } else if (val !== -1 && (acc.length === 0 || acc[acc.length - 1].opacity !== 1)) {
                acc.push({ value: idx, opacity: 1 });
            }
            return acc;
        }, []);


        let zonesLo = groupedLoCardio.map((zone, i, arr) => {
            return {
                opacity: zone.opacity,
                color: Highcharts.color(CHART_COLOR_BPM).setOpacity(zone.opacity).get('rgba'),
                fillColor: Highcharts.color(CHART_COLOR_BPM).setOpacity(zone.opacity).get('rgba'),
                value: xAxisData[arr[i + 1]?.value || loCardio.length - 1]
            }
        });


        return {
            ...BaseOptions,
            chart: { ...BaseOptions.chart, type: 'line', name: 'speedbpm' },
            title: {
                ...BaseOptions.title,
                useHTML: true,
                text: `
    <span style="color: ${adaptiveDisplay ? '#222' : isSmallScreen && !isUserTypePolo ? CHART_COLOR_BPM : "white"};">
      ${t(isUserTypePolo ? 'map:heartRate' : 'graph:bpm')} - 
    </span> 
    <span style="color: ${adaptiveDisplay ? '#222' : isSmallScreen && !isUserTypePolo ? CHART_COLOR_SPEED : "white"};">
      ${t('map:speed')}
    </span>
  `,
            },
            yAxis: [{ // Primary yAxis
                ...BaseOptions.yAxis,
                tickAmount: 4,
                min: 0,
                max: 250,
                tickPositions: [0, 62, 125, 187, 250], // Exactly 4 intervals, 5 ticks
                title: {
                    text: t('units:bpm'),
                    style: {
                        color: CHART_COLOR_BPM
                    },
                },
                labels: {
                    style: {
                        color: CHART_COLOR_BPM
                    }
                },
                opposite: false
            }, { // Secondary yAxis
                ...BaseOptions.yAxis,
                tickAmount: 4,
                // 1. Critical: Prevent Highcharts from syncing this axis scale with others
                alignTicks: false,

                // 2. Lock the max to your DB value (e.g., 183.33)
                max: getMaxYValueSpeed,

                // 3. Prevent rounding up to the next "nice" number (like 240)
                endOnTick: false,
                startOnTick: false,

                // 4. Force exact positions so it doesn't calculate 0, 60, 120, 180, 240
                tickPositions: [
                    0,
                    getMaxYValueSpeed * 0.33,
                    getMaxYValueSpeed * 0.66,
                    getMaxYValueSpeed
                ],

                title: {

                    text: t('graph:vitesse') + ` (${getUserSpeedUnit})`,
                    style: {
                        color: CHART_COLOR_SPEED
                    },

                },
                labels: {
                    style: {
                        color: CHART_COLOR_SPEED
                    },
                    /* @ts-ignore */
                    formatter: function () {
                        /* @ts-ignore */
                        return toNumber(getSpeedForChart(toNumber(this.value), getSpeedUnit()?.value ?? "")).toFixed(0)
                    }
                },
            }],
            xAxis: {
                plotBands,
                events: {
                    afterSetExtremes: (event: Highcharts.AxisSetExtremesEventObject) => {

                        let min = event.min
                        let max = event.max

                        if (min < 0) {
                            max -= Math.abs(min)
                            min = 0
                        }

                        // Early exit for empty charts (no data points)
                        const axis = event.target as unknown as Highcharts.Axis;
                        const chart = axis.chart;
                        const hasData = chart.series?.some(series => series.data.length > 0);

                        if (isNull(event?.dataMin) || isNull(event?.dataMax)) {
                            return
                        }

                        const isZoomed = min > event?.dataMin || max < event?.dataMax;

                        onExtremesChanged(max - min)


                        if (hasData) setIsZoomActive(isZoomed)

                        // ✅ Sync to other charts on mobile pinch (but skip if programmatic)
                        if (!isChukkerSelectionEnabled && event.trigger) {

                            syncZoomAcrossCharts(min, max);
                        }
                    }
                },
                crosshair: {
                    width: 1,
                },
                type: 'linear',
                labels: {
                    style: {
                        color: labelsColor
                    },
                    format: `{value} ${scaleOption === 'TIME' ? ' mn' : metric === 'metric' ? ' km' : ' mi'}`,

                }
            },
            tooltip: {
                ...BaseOptions.tooltip, shared: true,
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    let ySpeed = get(this, 'points[1].y', 0)
                    let data = get(this, 'points[0].series.processedYData', [])
                    let data1 = get(this, 'points[1].series.processedYData', [])

                    if (isZoomActive) {
                        let minVal = min(data)
                        let maxVal = max(data)
                        let avg = Math.round(mean(data))

                        let minVal1 = getSpeedTooltipForChart(toNumber(getSpeedForChart(toNumber(min(data1)), getSpeedUnit()?.value ?? "")), getSpeedUnit()?.value ?? "")
                        let maxVal1 = getSpeedTooltipForChart(toNumber(getSpeedForChart(toNumber(max(data1)), getSpeedUnit()?.value ?? "")), getSpeedUnit()?.value ?? "")
                        let avg1 = getSpeedTooltipForChart(toNumber(getSpeedForChart(toNumber(Math.round(mean(data1))), getSpeedUnit()?.value ?? "")), getSpeedUnit()?.value ?? "")

                        return `${this?.points && this?.points[0]?.y?.toString().concat(` ${t('units:bpm')}`)}  <span style="font-size:10px;font-weight:400"> (${t('minimumAbbreviation')}: ${minVal} -  ${t('meanAbbreviation')}: ${avg} -  ${t('maximumAbbreviation')}: ${maxVal})</span> </br>
            ${getSpeedTooltipForChart(toNumber(getSpeedForChart(ySpeed, getSpeedUnit()?.value ?? "")), getSpeedUnit()?.value ?? "")} 
             <span style="font-size:10px;font-weight:400"> (${t('minimumAbbreviation')}: ${minVal1} -  ${t('meanAbbreviation')}: ${avg1} -  ${t('maximumAbbreviation')}: ${maxVal1})</span>
            `
                    } else {
                        let str = ''
                        str += this?.points && this?.points[0]?.y + ` ${t('units:bpm')}` + ' - '
                        /* @ts-ignore */
                        str += this?.points && getSpeedTooltipForChart(getSpeedForChart(ySpeed, getSpeedUnit()?.value ?? ""), getSpeedUnit()?.value ?? "")
                        return str
                    }


                },
            },
            series: [
                {
                    yAxis: 0,
                    data: isEmpty(checkIfDataAllZero(bpm)) ? [] : mergeArr(scaleOption === 'DISTANCE' ? distanceConverted : timeConverted, bpm),
                    zoneAxis: "x",
                    zones: zonesLo.map((zone) => ({ ...zone })),
                    color: CHART_COLOR_BPM,
                    point: {},
                    nextChartData: true,
                },
                {
                    yAxis: 1,
                    data: isEmpty(checkIfDataAllZero(speed)) ? [] : mergeArr(scaleOption === 'DISTANCE' ? distanceConverted : timeConverted, speed),
                    point: {},
                    nextChartData: true,
                },
            ]
        }
    }, [bpm, speed, distanceConverted, timeConverted, scaleOption, isZoomActive, isFinishLineActive, t, isChukkerSelectionEnabled, plotBands, plotBandId, editModeEnabled, isSmallScreen, getMaxYValueSpeed])

    const optionsStride = useMemo(() => {
        return {
            ...BaseOptions,
            chart: { ...BaseOptions.chart, type: 'line' },
            title: {
                ...BaseOptions.title,
                useHtml: true,
                style: {
                    color: adaptiveDisplay ? '#222' : "white"
                },
                text: isUserTypePolo ? t('graph:strLenFreTitle') : `
  <span style="color: ${adaptiveDisplay ? '#222' : (!isSmallScreen && !isUserTypePolo) ? "white" : CHART_COLOR_STRIDE_LENGTH};">
    ${t('graph:amplitude')}
  </span> - 
  <span style="color: ${adaptiveDisplay ? '#222' : (!isSmallScreen && !isUserTypePolo) ? "white" : CHART_COLOR_STRIDE_FREQUENCY};">
    ${t('graph:cadence')}
  </span>
`
            },
            yAxis: [{ // Primary yAxis
                ...BaseOptions.yAxis,
                min: 0,
                max: 150,
                tickAmount: 4,
                endOnTick: false,
                title: isSmallScreen && !isUserTypePolo ? undefined : {
                    text: t('graph:cadence') + ` (${t('graph:foulees')})`,
                    style: {
                        color: CHART_COLOR_STRIDE_FREQUENCY
                    }
                },
                labels: {
                    style: {
                        color: CHART_COLOR_STRIDE_FREQUENCY
                    },
                    /* @ts-ignore */
                    formatter: function () {
                        /* @ts-ignore */
                        return Math.round(toNumber(this.value) / 50)
                    }
                },
                opposite: false
            }, { // Primary yAxis
                ...BaseOptions.yAxis,
                min: 0,
                max: 170,
                endOnTick: false,
                tickAmount: 4,
                title: isSmallScreen && !isUserTypePolo ? undefined : {
                    text: t('graph:amplitude') + ` (${getUserAmplitudeUnit})`,
                    style: {
                        color: CHART_COLOR_STRIDE_LENGTH
                    }
                },
                labels: {
                    style: {
                        color: CHART_COLOR_STRIDE_LENGTH
                    },
                    /* @ts-ignore */
                    formatter: function () {
                        /* @ts-ignore */
                        return Math.round(toNumber(convertStrideLength(metric, toNumber(this.value))))
                    }
                },
                opposite: !isUserTypePolo,
            }],
            xAxis: {
                events: {
                    afterSetExtremes: (event: Highcharts.AxisSetExtremesEventObject) => {

                        let min = event.min
                        let max = event.max


                        if (min < 0) {
                            max -= Math.abs(min)
                            min = 0
                        }

                        // Early exit for empty charts (no data points)
                        const axis = event.target as unknown as Highcharts.Axis;
                        const chart = axis.chart;
                        const hasData = chart.series?.some(series => series.data.length > 0);

                        if (isNull(event?.dataMin) || isNull(event?.dataMax)) {
                            return
                        }

                        const isZoomed = min > event?.dataMin || max < event?.dataMax;

                        onExtremesChanged(max - min)


                        if (hasData) setIsZoomActive(isZoomed)

                        // ✅ Sync to other charts on mobile pinch (but skip if programmatic)
                        if (!isChukkerSelectionEnabled && event.trigger) {

                            syncZoomAcrossCharts(min, max);
                        }
                    }
                },
                crosshair: {
                    width: 1,
                },
                type: 'linear',
                labels: {
                    style: {
                        color: labelsColor
                    },
                    format: `{value} ${scaleOption === 'TIME' ? ' mn' : metric === 'metric' ? ' km' : ' mi'}`,

                }
            },
            tooltip: {
                ...BaseOptions.tooltip, shared: true,
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    let data = get(this, 'points[0].series.processedYData', [])
                    let data1 = get(this, 'points[1].series.processedYData', [])

                    if (isZoomActive) {
                        let minVal = (toNumber(min(data)) / 50).toFixed(1)
                        let maxVal = (toNumber(max(data)) / 50).toFixed(1)
                        let avg = (mean(data) / 50).toFixed(1)

                        let minVal1 = convertStrideLength(metric, min(data1))
                        let maxVal1 = convertStrideLength(metric, max(data1))
                        let avg1 = convertStrideLength(metric, mean(data1))

                        return `${this?.points && (toNumber(this?.points[0]?.y) / 50).toFixed(1) + " " + t('graph:foulees') + ' - '} <span style="font-size:10px;font-weight:400"> (${t('minimumAbbreviation')}: ${minVal} -  ${t('meanAbbreviation')}: ${avg} -  ${t('maximumAbbreviation')}: ${maxVal})</span> </br>
            ${this?.points && convertStrideLength(metric, this?.points[1]?.y) + " " + t(metric === 'imperial' ? 'graph:pieds' : 'graph:metres')}
            <span style="font-size:10px;font-weight:400"> (${t('minimumAbbreviation')}: ${minVal1} -  ${t('meanAbbreviation')}: ${avg1} -  ${t('maximumAbbreviation')}: ${maxVal1})</span>
            `

                    } else {
                        let str = ''
                        str += this?.points && (toNumber(this?.points[0]?.y) / 50) + " " + t('graph:foulees') + ' - '
                        str += this?.points && convertStrideLength(metric, this?.points[1]?.y) + " " + t(metric === 'imperial' ? 'graph:pieds' : 'graph:metres')
                        return str
                    }


                },
            },
            series: [
                {
                    yAxis: 0,
                    data: isEmpty(checkIfDataAllZero(strideFrequency)) ? [] : mergeArr(scaleOption === 'DISTANCE' ? distanceConverted : timeConverted, strideFrequency),
                    color: CHART_COLOR_STRIDE_FREQUENCY,
                    point: {},
                    nextChartData: true,
                },
                {
                    yAxis: 1,
                    data: isEmpty(checkIfDataAllZero(strideLength)) ? [] : mergeArr(scaleOption === 'DISTANCE' ? distanceConverted : timeConverted, strideLength),
                    color: CHART_COLOR_STRIDE_LENGTH,
                    point: {},
                    nextChartData: true,
                },
            ]
        }
    }, [isChukkerSelectionEnabled, strideLength, strideFrequency, distanceConverted, timeConverted, scaleOption, isZoomActive, isFinishLineActive, t, isSmallScreen])

    const optionsStrideFreq = useMemo(() => {
        return {
            ...BaseOptions,
            chart: {
                ...BaseOptions.chart,

            },
            title: {
                ...BaseOptions.title,
                text: t('graph:cadence'),
                style: { color: adaptiveDisplay ? '#222' : isSmallScreen ? CHART_COLOR_STRIDE_FREQUENCY : "white" }
            },
            yAxis: [{ // Primary yAxis
                ...BaseOptions.yAxis,
                min: 0,
                max: 150,
                tickAmount: 4,
                title: {
                    text: isSmallScreen ? undefined : t('graph:cadence'),
                    style: {
                        color: CHART_COLOR_STRIDE_FREQUENCY
                    }
                },

                labels: {
                    style: {
                        color: CHART_COLOR_STRIDE_FREQUENCY
                    },
                    /* @ts-ignore */
                    formatter: function () {
                        /* @ts-ignore */
                        return Math.round(toNumber(this.value) / 50)
                    }
                },

                opposite: false
            }],
            xAxis: {
                events: {
                    afterSetExtremes: (event: Highcharts.AxisSetExtremesEventObject) => {

                        let min = event.min
                        let max = event.max


                        if (min < 0) {
                            max -= Math.abs(min)
                            min = 0
                        }
                        // Early exit for empty charts (no data points)
                        const axis = event.target as unknown as Highcharts.Axis;
                        const chart = axis.chart;
                        const hasData = chart.series?.some(series => series.data.length > 0);


                        if (isNull(event?.dataMin) || isNull(event?.dataMax)) {
                            return
                        }

                        const isZoomed = min > event?.dataMin || max < event?.dataMax;

                        onExtremesChanged(max - min)


                        if (hasData) setIsZoomActive(isZoomed)

                        // ✅ Sync to other charts on mobile pinch (but skip if programmatic)
                        if (!isChukkerSelectionEnabled && event.trigger) {

                            syncZoomAcrossCharts(min, max);
                        }
                    }

                },
                crosshair: {
                    width: 1,
                },
                type: 'linear',
                labels: {
                    style: {
                        color: labelsColor
                    },
                    format: `{value} ${scaleOption === 'TIME' ? ' mn' : metric === 'metric' ? ' km' : ' mi'}`,

                }
            },
            tooltip: {
                ...BaseOptions.tooltip, shared: true,
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    let data: number[] = get(this, 'series.processedYData', [])
                    if (isZoomActive) {
                        if (data.length > 0) {
                            let minVal = (toNumber(min(data)) / 50).toFixed(2)
                            let maxVal = (toNumber(max(data)) / 50).toFixed(2)
                            let avg = (toNumber(mean(data)) / 50).toFixed(2)
                            let str = ''
                            str += (toNumber(this?.y ?? 0) / 50).toFixed(2) + " " + t('graph:foulees')
                            return `${str}
              <br/>
             <span style="font-size:10px;font-weight:400"> (${t('minimumAbbreviation')}: ${minVal} -  ${t('meanAbbreviation')}: ${avg} -  ${t('maximumAbbreviation')}: ${maxVal})</span>`

                        } else {
                            let str = ''
                            str += (toNumber(this?.y ?? 0) / 50).toFixed(2) + " " + t('graph:foulees')
                            return str
                        }

                    } else {
                        let str = ''
                        str += (toNumber(this?.y ?? 0) / 50).toFixed(2) + " " + t('graph:foulees')
                        return str
                    }


                },
            },
            series: [
                {
                    data: isEmpty(checkIfDataAllZero(strideFrequency)) ? [] : mergeArr(scaleOption === 'DISTANCE' ? distanceConverted : timeConverted, strideFrequency),
                    color: CHART_COLOR_STRIDE_FREQUENCY,
                    fillColor: {
                        linearGradient: {
                            x1: 0,
                            y1: 0,
                            x2: 0,
                            y2: 1
                        },
                        stops: [
                            [0, new (Highcharts as any).Color(CHART_COLOR_STRIDE_FREQUENCY).setOpacity(0.4).get('rgba')],
                            [1, new (Highcharts as any).Color(CHART_COLOR_STRIDE_FREQUENCY).setOpacity(0.2).get('rgba')]
                        ]
                    },
                    point: {},
                },

            ]
        }
    }, [strideFrequency, distanceConverted, timeConverted, scaleOption, isZoomActive, isFinishLineActive, t, isChukkerSelectionEnabled, isSmallScreen])

    const optionsStrideLength = useMemo(() => {
        return {
            ...BaseOptions,
            chart: {
                ...BaseOptions.chart,

            },
            title: {
                ...BaseOptions.title, text: t('graph:amplitude'), style: {
                    ...(BaseOptions.title?.style || {}),
                    color: adaptiveDisplay ? '#222' : isSmallScreen ? CHART_COLOR_STRIDE_LENGTH : "white"
                }
            },
            yAxis: [{ // Primary yAxis
                ...BaseOptions.yAxis,
                min: 0,
                max: 170,
                endOnTick: false,
                tickAmount: 4,
                title: {
                    text: isSmallScreen ? undefined : t('graph:amplitude'),
                    style: {
                        color: CHART_COLOR_STRIDE_LENGTH
                    },

                },
                labels: {
                    style: {
                        color: CHART_COLOR_STRIDE_LENGTH
                    },
                    /* @ts-ignore */
                    formatter: function () {
                        /* @ts-ignore */
                        return Math.round(toNumber(convertStrideLength(metric, toNumber(this.value))))
                    }
                },
            }],
            xAxis: {
                events: {
                    afterSetExtremes: (event: Highcharts.AxisSetExtremesEventObject) => {

                        let min = event.min
                        let max = event.max


                        if (min < 0) {
                            max -= Math.abs(min)
                            min = 0
                        }
                        // Early exit for empty charts (no data points)
                        const axis = event.target as unknown as Highcharts.Axis;
                        const chart = axis.chart;
                        const hasData = chart.series?.some(series => series.data.length > 0);

                        if (isNull(event?.dataMin) || isNull(event?.dataMax)) {
                            return
                        }

                        const isZoomed = min > event?.dataMin || max < event?.dataMax;

                        onExtremesChanged(max - min)


                        if (hasData) setIsZoomActive(isZoomed)

                        // ✅ Sync to other charts on mobile pinch (but skip if programmatic)
                        if (!isChukkerSelectionEnabled && event.trigger) {

                            syncZoomAcrossCharts(min, max);
                        }
                    }

                },
                crosshair: {
                    width: 1,
                },
                type: 'linear',
                labels: {
                    style: {
                        color: labelsColor

                    },
                    format: `{value} ${scaleOption === 'TIME' ? ' mn' : metric === 'metric' ? ' km' : ' mi'}`,

                }
            },
            tooltip: {
                ...BaseOptions.tooltip, shared: true,
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    let data = get(this, 'series.processedYData', [])
                    if (isZoomActive) {
                        if (data.length > 0) {
                            let minVal = convertStrideLength(metric, min(data))
                            let maxVal = convertStrideLength(metric, max(data))
                            let avg = convertStrideLength(metric, Math.round(mean(data)))
                            let str = ''
                            str += this?.points && convertStrideLength(metric, this?.points[0]?.y) + " " + t(metric === 'imperial' ? 'graph:pieds' : 'graph:metres')

                            return `${str}
              <br/>
             <span style="font-size:10px;font-weight:400"> (${t('minimumAbbreviation')}: ${minVal} -  ${t('meanAbbreviation')}: ${avg} -  ${t('maximumAbbreviation')}: ${maxVal})</span>`
                        } else {
                            let str = ''
                            str += this?.points && convertStrideLength(metric, this?.points[0]?.y) + " " + t(metric === 'imperial' ? 'graph:pieds' : 'graph:metres')
                            return str
                        }
                    } else {
                        let str = ''
                        str += this?.points && convertStrideLength(metric, this?.points[0]?.y) + " " + t(metric === 'imperial' ? 'graph:pieds' : 'graph:metres')
                        return str
                    }


                },
            },
            series: [

                {
                    data: isEmpty(checkIfDataAllZero(strideLength)) ? [] : mergeArr(scaleOption === 'DISTANCE' ? distanceConverted : timeConverted, strideLength),
                    color: CHART_COLOR_STRIDE_LENGTH,
                    fillColor: {
                        linearGradient: {
                            x1: 0,
                            y1: 0,
                            x2: 0,
                            y2: 1
                        },
                        stops: [
                            [0, new (Highcharts as any).Color(CHART_COLOR_STRIDE_LENGTH).setOpacity(0.4).get('rgba')],
                            [1, new (Highcharts as any).Color(CHART_COLOR_STRIDE_LENGTH).setOpacity(0.2).get('rgba')]
                        ]
                    },
                    point: {},
                },
            ]
        }
    }, [strideLength, distanceConverted, timeConverted, scaleOption, isZoomActive, isFinishLineActive, t, isChukkerSelectionEnabled, isSmallScreen])

    const resetZoom = () => {
        let charts = getCharts()
        charts.forEach(chart => chart?.xAxis[0].setExtremes(undefined, undefined))
        setIsZoomActive(false)
    }


    const handlePopperClose = () => {
        if (typeof setPlotBandId === 'function') setPlotBandId(null)
        if (typeof toggleChukkerEditMode === 'function') toggleChukkerEditMode(false)
        hidePopper()
    };

    const hidePopper = () => {
        if (!popupRef.current) return;

        // Get current y transform value (number)
        const currentY = gsap.getProperty(popupRef.current, "y");

        gsap.to(popupRef.current, {
            opacity: 0,
            y: toNumber(currentY) + 50,
            duration: 1,
            ease: "elastic.out(1, 0.5)",  // springy bounce effect

        });
    }


    return (
        <Box
            sx={{
                backgroundColor: adaptiveDisplay ? '#fff' : isUserTypePolo ? "rgba(128,128,128,.5)" : "-moz-initial",
                padding: 2,
                borderRadius: 1,
                width: printable ? '1100px' : '100%'
            }}
            id='graphs-section' display={'flex'} flexDirection={'column'} ref={divRef} position={'relative'}>
            {
                isZoomActive && <Box display={'flex'} justifyContent={'end'} mt={2}>
                    <Button onClick={resetZoom} startIcon={<Icon icon='ic:round-zoom-in-map'></Icon>} size='small'
                        variant='contained'>{t('resetZoom')}</Button>
                </Box>
            }

            <Hideable isVisible={mergedBpmSpeed}>
                <>
                    {displayAlertBadCardio && <Typography sx={{ textAlign: 'center' }}
                        variant="caption"> {t("alerts:percentageGoodCardio:bad")}</Typography>}
                    <RenderHintValue hintValue={hintValue} scaleOption={scaleOption} adaptiveDisplay={adaptiveDisplay} />
                    <HighchartsReact
                        ref={bpmSpeedRef}
                        highcharts={Highcharts}
                        options={optionsBpmSpeed}
                        containerProps={{ style: { height: '240px' } }}
                        key={isSmallScreen ? 'true' : 'false'}
                    />
                </>

            </Hideable>

            <Hideable isVisible={userSettings.display_graph_speed && !mergedBpmSpeed}>
                <RenderHintValue hintValue={hintValue} scaleOption={scaleOption} adaptiveDisplay={adaptiveDisplay} />
                <HighchartsReact
                    ref={speedRef}
                    highcharts={Highcharts}
                    options={optionsSpeed}
                    containerProps={{ style: { height: '240px' } }}
                    key={isSmallScreen ? 'true' : 'false'}

                />

            </Hideable>
            <Hideable isVisible={userSettings.display_graph_bpm && !mergedBpmSpeed}>
                <>
                    {displayAlertBadCardio && <Typography color={adaptiveDisplay ? 'black' : 'white'} sx={{ textAlign: 'center' }}
                        variant="caption"> {t("alerts:percentageGoodCardio:bad")}</Typography>}
                    <HighchartsReact
                        ref={bpmRef}
                        highcharts={Highcharts}
                        options={optionsBpm}
                        containerProps={{ style: { height: '240px' } }}
                        key={isSmallScreen ? 'true' : 'false'}

                    />

                </>
            </Hideable>


            <Hideable isVisible={mergedStride}>
                <HighchartsReact
                    ref={strideRef}
                    highcharts={Highcharts}
                    options={optionsStride}
                    containerProps={{ style: { height: '240px', width: '100%' } }}
                    key={isSmallScreen ? 'true' : 'false'}

                />
            </Hideable>


            <Hideable
                isVisible={userSettings.display_graph_stride_frequency && !mergedStride}>
                <HighchartsReact
                    ref={freqRef}
                    highcharts={Highcharts}
                    options={optionsStrideFreq}
                    containerProps={{ style: { height: '240px' } }}
                    key={isSmallScreen ? 'true' : 'false'}

                />
            </Hideable>
            <Hideable
                isVisible={userSettings.display_graph_stride_length && !mergedStride}>

                <HighchartsReact
                    ref={lengthRef}
                    highcharts={Highcharts}
                    options={optionsStrideLength}
                    containerProps={{ style: { height: '240px' } }}
                    key={isSmallScreen ? 'true' : 'false'}

                />
            </Hideable>

            <Paper
                ref={popupRef}
                elevation={8}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    padding: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: (theme) => theme.shadows[8],
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    minWidth: 160,
                    opacity: 0,
                    userSelect: 'none',
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems={'center'}>
                    <Typography variant='subtitle2'>{plotBandLabelText}</Typography>
                    <IconButton
                        size="small"
                        aria-label="close"
                        onClick={handlePopperClose}
                        sx={{ color: 'text.secondary' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Stack spacing={1}>
                    <Button
                        variant="text"
                        size="small"
                        color="primary"
                        startIcon={<Edit />}
                        sx={{ justifyContent: 'flex-start' }}
                        onClick={() => {
                            typeof toggleChukkerEditMode === 'function' && toggleChukkerEditMode(true)
                            hidePopper()
                        }}
                    >
                        {t('button:edit')}
                    </Button>
                    <Button
                        variant="text"
                        size="small"
                        color="error"
                        startIcon={<GridDeleteIcon />}
                        sx={{ justifyContent: 'flex-start' }}
                        onClick={() => {
                            if (plotBandId && typeof onDeleteChukker === 'function') onDeleteChukker(plotBandId)

                        }}
                    >
                        {t('button:delete')}
                    </Button>
                </Stack>
            </Paper>


            <Snackbar
                open={editModeEnabled}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                onClose={() => {
                    if (typeof toggleChukkerEditMode === 'function') toggleChukkerEditMode(false);
                    if (typeof setPlotBandId === 'function') setPlotBandId(null);
                }}
                ContentProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                }}
                message={
                    <Paper
                        elevation={4}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            px: 2,
                            py: 1,
                            backgroundColor: 'background.paper',
                        }}
                    >
                        {t('editInProgress')}
                        <Button
                            color="secondary"
                            size="small"
                            onClick={() => {
                                if (typeof toggleChukkerEditMode === 'function') toggleChukkerEditMode(false);
                                if (typeof setPlotBandId === 'function') setPlotBandId(null);
                            }}
                        >
                            {t('button:cancel')}
                        </Button>
                    </Paper>
                }
            />


        </Box>


    )
}

function RenderHintValue({ hintValue, scaleOption, adaptiveDisplay }: { adaptiveDisplay: boolean, hintValue?: string, scaleOption: Scale }) {
    const { t } = useTranslation()
    if (!hintValue) {
        return null
    }
    return (
        <Box display={'flex'} justifyContent={'center'}>
            <Typography variant='caption' color={adaptiveDisplay ? 'black' : 'white'} sx={{ fontWeight: 600, display: 'flex', gap: 1 }}>
                {t(scaleOption === 'DISTANCE' ? 'graph:distance' : 'graph:time')} :
                <Typography variant='caption' fontWeight={500}>{hintValue}</Typography>
            </Typography>
        </Box>
    )
}

export default Graphs

